import YTPictureInPicture from './youtube-picture-in-picture.mjs';
import { DataAttrParser } from '@progress-wad/data-attr-parser/index.mjs';

const defaultOptions = {
  btnClass: 'yl-btn',
  activeClass: 'is-active',
};

class YouTubeLiteEmbed {
  constructor(element, options) {
    this.element = element;
    this.options = {
      ...defaultOptions,
      ...options,
      ...DataAttrParser.parse(element.dataset, [ 'youtube' ]).youtube
    };

    this.videoId = this.options.id;

    // Generate unique id for the video instance, necessary for the iframe API init
    this.id = `video_${crypto.randomUUID().slice(0, 4)}`;

    this.iframe = document.createElement('div');
    this.iframe.id = this.id;
    this.element.append(this.iframe);

    this.params = {
      ...Object.fromEntries(
        new URLSearchParams(this.options.params || [])
      ),
      autoplay: 1,
      createdByApi: 1
    };

    this.addThumbnail();
    this.addPlayButton();
    this.attachEventListeners();
  }

  static addPreconnect(url) {
    const linkEl = document.createElement('link');
    linkEl.rel = 'preconnect';
    linkEl.href = url;
    document.head.append(linkEl);
  }

  static preconnectToDomains() {
    if (YouTubeLiteEmbed.arePreconnectsAdded) return;
    YouTubeLiteEmbed.addPreconnect('https://www.youtube.com');
    YouTubeLiteEmbed.addPreconnect('https://www.google.com');
    YouTubeLiteEmbed.arePreconnectsAdded = true;
  }

  static loadIframeAPI() {
    if (YouTubeLiteEmbed.iframeAPILoaded) return;

    let existingHandler = () => {};

    if (window.onYouTubeIframeAPIReady && typeof window.onYouTubeIframeAPIReady === 'function') {
      existingHandler = window.onYouTubeIframeAPIReady;
    }

    window.onYouTubeIframeAPIReady = () => {
      YouTubeLiteEmbed.iframeAPILoaded = true;
      window.dispatchEvent(new Event('YouTubeIframeAPIReady'));
      existingHandler();
    };

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.append(tag);
  }

  attachEventListeners() {
    this.element.addEventListener(
      'pointerover',
      YouTubeLiteEmbed.preconnectToDomains,
      { once: true }
    );

    this.element.addEventListener('click', this.clickEventHandler.bind(this), {
      once: true,
    });
  }

  addThumbnail() {
    if (!this.options.poster || this.options.poster.toLowerCase() === 'hd') {
      const quality = this.options.poster === 'hd' ? 'maxresdefault' : 'hqdefault';
      this.element.style.backgroundImage = `url(https://i.ytimg.com/vi/${this.videoId}/${quality}.jpg)`;
      return;
    }

    this.element.style.backgroundImage = `url(${this.options.poster})`;
  }

  addPlayButton() {
    let btnElement = document.createElement('button');
    btnElement.type = 'button';
    btnElement.classList.add(this.options.btnClass);
    btnElement.setAttribute(
      'aria-label',
      `Play video${this.options.label ? `: ${this.options.label}` : ''}`
    );
    this.element.append(btnElement);
  }

  onPlayerReady() {
    this.play();
    this.player.getIframe().player = this.player;
  }

  createPlayer() {
    this.player = new YT.Player(this.id, {
      videoId: this.videoId,
      playerVars: this.params,
      events: {
        onReady: this.onPlayerReady.bind(this)
      }
    });

    if (this.options.behaviour && this.options.behaviour.toLowerCase() === 'pip') {
      window.YTPictureInPicture.activeInstance = new YTPictureInPicture(this.element, this.player);
    }
  }

  play() {
    if (this.player.playVideo) this.player.playVideo();
    this.element.classList.add(this.options.activeClass);
    this.iframe.focus();
  }

  pause() {
    this.player.pauseVideo();
    this.element.classList.remove(this.options.activeClass);
  }

  clickEventHandler() {
    if (!YouTubeLiteEmbed.iframeAPILoaded) {
      window.addEventListener('YouTubeIframeAPIReady', this.createPlayer.bind(this));
      YouTubeLiteEmbed.loadIframeAPI();
      return;
    }

    this.createPlayer();
  }
}

export default YouTubeLiteEmbed;
